import { HeroData } from '../models/HeroData';

// OpenDota numeric ID to hero string ID mapping
// Source: https://github.com/odota/dotaconstants/blob/master/build/heroes.json
const OPENDOTA_HERO_ID_MAP = {
  1: 'antimage', 2: 'axe', 3: 'bane', 4: 'bloodseeker', 5: 'crystal_maiden',
  6: 'drow_ranger', 7: 'earthshaker', 8: 'juggernaut', 9: 'mirana', 10: 'morphling',
  11: 'nevermore', 12: 'phantom_lancer', 13: 'puck', 14: 'pudge', 15: 'razor',
  16: 'sand_king', 17: 'storm_spirit', 18: 'sven', 19: 'tiny', 20: 'vengefulspirit',
  21: 'windrunner', 22: 'zuus', 23: 'kunkka', 25: 'lina', 26: 'lion',
  27: 'shadow_shaman', 28: 'slardar', 29: 'tidehunter', 30: 'witch_doctor', 31: 'lich',
  32: 'riki', 33: 'enigma', 34: 'tinker', 35: 'sniper', 36: 'necrolyte',
  37: 'warlock', 38: 'beastmaster', 39: 'queenofpain', 40: 'venomancer', 41: 'faceless_void',
  42: 'skeleton_king', 43: 'death_prophet', 44: 'phantom_assassin', 45: 'pugna', 46: 'templar_assassin',
  47: 'viper', 48: 'luna', 49: 'dragon_knight', 50: 'dazzle', 51: 'rattletrap',
  52: 'leshrac', 53: 'furion', 54: 'life_stealer', 55: 'dark_seer', 56: 'clinkz',
  57: 'omniknight', 58: 'enchantress', 59: 'huskar', 60: 'night_stalker', 61: 'broodmother',
  62: 'bounty_hunter', 63: 'weaver', 64: 'jakiro', 65: 'batrider', 66: 'chen',
  67: 'spectre', 68: 'ancient_apparition', 69: 'doom_bringer', 70: 'ursa', 71: 'spirit_breaker',
  72: 'gyrocopter', 73: 'alchemist', 74: 'invoker', 75: 'silencer', 76: 'obsidian_destroyer',
  77: 'lycan', 78: 'brewmaster', 79: 'shadow_demon', 80: 'lone_druid', 81: 'chaos_knight',
  82: 'meepo', 83: 'treant', 84: 'ogre_magi', 85: 'undying', 86: 'rubick',
  87: 'disruptor', 88: 'nyx_assassin', 89: 'naga_siren', 90: 'keeper_of_the_light', 91: 'wisp',
  92: 'visage', 93: 'slark', 94: 'medusa', 95: 'troll_warlord', 96: 'centaur',
  97: 'magnataur', 98: 'shredder', 99: 'bristleback', 100: 'tusk', 101: 'skywrath_mage',
  102: 'abaddon', 103: 'elder_titan', 104: 'legion_commander', 105: 'techies', 106: 'ember_spirit',
  107: 'earth_spirit', 108: 'abyssal_underlord', 109: 'terrorblade', 110: 'phoenix', 111: 'oracle',
  112: 'winter_wyvern', 113: 'arc_warden', 114: 'monkey_king', 119: 'dark_willow', 120: 'pangolier',
  121: 'grimstroke', 123: 'hoodwink', 126: 'void_spirit', 128: 'snapfire', 129: 'mars',
  131: 'ringmaster', // Ringmaster
  135: 'dawnbreaker', 136: 'marci', 137: 'primal_beast', 138: 'muerta', 145: 'kez',
  155: 'largo' // Largo (Patch 7.40, December 2025)
};

class MatchDataService {
  // Cache directory handle in memory (survives until browser tab closed)
  static cachedDirectoryHandle = null;

  static async pullMatchData(matchId) {
    if (!matchId || typeof matchId !== 'string') {
      throw new Error('Valid match ID is required');
    }

    try {
      // STEP 1: Try OpenDota via Firebase Cloud Function
      const functionUrl = process.env.REACT_APP_PULL_MATCH_DATA_URL ||
                         'https://us-central1-draftsimulatorapp.cloudfunctions.net/pullMatchData';

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ matchId }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          console.log('✅ Match data loaded from OpenDota');
          result.data.source = 'OpenDota';
          return result.data;
        }
      }

      // OpenDota failed - fall through to local file check
      console.log('OpenDota not available, checking local files...');

    } catch (error) {
      console.log('OpenDota error, checking local files...', error.message);
    }

    // STEP 2: Try local dotaclient folder
    try {
      const localData = await this.pullFromLocalDotaClient(matchId);
      console.log('✅ Match data loaded from local file');
      localData.source = 'Local File';
      return localData;
    } catch (localError) {
      // Both methods failed
      throw new Error(`Match ${matchId} not found on OpenDota or in local files. ${localError.message}`);
    }
  }

  static async pullFromLocalDotaClient(matchId) {
    // Check if File System Access API is supported
    if (!window.showDirectoryPicker) {
      throw new Error('Local file access not supported in this browser. Please use Chrome or Edge.');
    }

    try {
      // Get directory handle (cached or prompt user)
      const directoryHandle = await this.getDotaClientDirectory();

      // Search for file matching pattern: live_draft_ygbuff2_{matchId}_*.json
      const matchingFiles = [];

      for await (const entry of directoryHandle.values()) {
        if (entry.kind === 'file' &&
            entry.name.startsWith(`live_draft_ygbuff2_${matchId}_`) &&
            entry.name.endsWith('.json')) {
          matchingFiles.push(entry);
        }
      }

      if (matchingFiles.length === 0) {
        throw new Error(`No draft file found for Match ID ${matchId} in selected folder`);
      }

      // Use the most recent file (sort by filename - timestamp is in filename)
      matchingFiles.sort((a, b) => b.name.localeCompare(a.name));
      const latestFile = matchingFiles[0];

      // Read and parse the file
      const fileHandle = await directoryHandle.getFileHandle(latestFile.name);
      const file = await fileHandle.getFile();
      const text = await file.text();
      const data = JSON.parse(text);

      console.log(`📁 Loaded local file: ${latestFile.name}`);

      return data;

    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Folder selection cancelled');
      }
      throw error;
    }
  }

  static async getDotaClientDirectory() {
    // Check if we have a cached directory handle from this session
    if (this.cachedDirectoryHandle) {
      try {
        const permission = await this.cachedDirectoryHandle.queryPermission({ mode: 'read' });
        if (permission === 'granted') {
          console.log('✅ Using cached directory (this session)');
          return this.cachedDirectoryHandle;
        }
      } catch (error) {
        console.log('⚠️ Cached directory handle invalid');
        this.cachedDirectoryHandle = null;
      }
    }

    // No valid cache - show folder picker
    console.log('📁 Select your draft_outputs folder...');
    const directoryHandle = await window.showDirectoryPicker({
      mode: 'read',
      startIn: 'documents',
    });

    // Cache for this session
    this.cachedDirectoryHandle = directoryHandle;
    console.log('✅ Directory cached for this session');

    return directoryHandle;
  }

  // Fallback method to look for the most recent match data file
  static async pullMatchDataFromFile(matchId) {
    try {
      // Since we can't directly access the file system from browser,
      // we'll use a trick - try to fetch the file via the dev server
      // First, we need to find the most recent file
      
      // Try to fetch a few possible recent timestamps
      const now = Date.now();
      const possibleTimestamps = [];
      
      // Generate possible timestamps for the last 10 minutes
      for (let i = 0; i < 10; i++) {
        possibleTimestamps.push(now - (i * 60000)); // Every minute back
      }
      
      // Try to fetch each possible file
      for (const timestamp of possibleTimestamps) {
        try {
          const fileName = `Match_${matchId}_Draft_2025-09-07_${timestamp}.json`;
          const response = await fetch(`/data/${fileName}`);
          
          if (response.ok) {
            const data = await response.json();
            return data;
          }
        } catch (e) {
          // Continue trying other timestamps
          continue;
        }
      }
      
      // If no specific timestamp works, try a generic pattern
      // This will require the file to be copied to public/data folder
      throw new Error('MANUAL_PROCESS_REQUIRED');
      
    } catch (error) {
      throw new Error('MANUAL_PROCESS_REQUIRED');
    }
  }

  /**
   * Extract laning matchup data from OpenDota match data
   * Maps heroes to Safe/Mid/Off lanes for Radiant and Dire teams
   *
   * CRITICAL: OpenDota lane numbers are team-relative!
   * - Radiant lane=1 is bottom-right (Radiant safe lane)
   * - Dire lane=1 is top-left (Dire safe lane)
   * - These face OPPOSITE sides of the map!
   *
   * Physical map layout:
   * - Top-left: Radiant OFF vs Dire SAFE
   * - Center: Radiant MID vs Dire MID
   * - Bottom-right: Radiant SAFE vs Dire OFF
   *
   * Returns practiceMatchups format compatible with PracticeMatchup component
   */
  static extractLaningMatchups(matchData) {
    // Check if we have the originalData from the Cloud Function
    const openDotaData = matchData?.originalData || matchData;

    if (!openDotaData || !openDotaData.players || !Array.isArray(openDotaData.players)) {
      console.warn('⚠️ No players array found in match data');
      return null;
    }

    // Initialize matchup structure: Safe (2v2), Mid (1v1), Off (2v2)
    // Store by physical map position (what actually faces each other)
    const matchups = {
      safe: { radiant: [], dire: [] },  // Radiant safe (lane=1) faces Dire off (lane=3)
      mid: { radiant: [], dire: [] },    // Radiant mid (lane=2) faces Dire mid (lane=2)
      off: { radiant: [], dire: [] }     // Radiant off (lane=3) faces Dire safe (lane=1)
    };

    console.log('🔍 Processing players for lane assignments:', openDotaData.players.length, 'players');

    openDotaData.players.forEach((player, index) => {
      const { hero_id, lane, lane_role, player_slot } = player;

      // Determine team from player_slot (0-127 = Radiant, 128-255 = Dire)
      const isRadiant = player_slot < 128;
      const team = isRadiant ? 'radiant' : 'dire';

      console.log(`Player ${index + 1}:`, {
        hero_id,
        lane,
        lane_role,
        player_slot,
        team,
        heroStringId: OPENDOTA_HERO_ID_MAP[hero_id]
      });

      if (!hero_id) {
        console.warn(`⚠️ Player ${index + 1} has no hero_id`);
        return;
      }

      // Determine physical map lane - lane number is SAME for both teams!
      // lane: 1 = one physical lane, 2 = mid, 3 = another physical lane
      // lane_role: 1=safe carry, 2=mid, 3=offlane, 4=jungle/roam/support
      let laneKey = null;

      if (lane === 2 && lane_role === 2) {
        // Mid lane - ONLY take true mid players (lane=2 AND lane_role=2)
        laneKey = 'mid';
      } else if (lane === 1) {
        // Lane 1 - both teams go in same row
        laneKey = 'safe';
      } else if (lane === 3) {
        // Lane 3 - both teams go in same row
        laneKey = 'off';
      } else if (lane_role === 4) {
        // Roaming support (position 4)
        if (lane === 1) {
          laneKey = 'safe';
          console.log(`🎯 Roamer detected (lane_role=4, lane=1) - assigning to safe lane`);
        } else if (lane === 3) {
          laneKey = 'off';
          console.log(`🎯 Roamer detected (lane_role=4, lane=3) - assigning to off lane`);
        } else if (lane === 2) {
          // Roamer went to mid - assign to offlane based on team
          // Radiant roamers → lane 3 (off), Dire roamers → lane 1 (off from Dire perspective)
          if (isRadiant) {
            laneKey = 'off'; // Radiant offlane is lane 3
            console.log(`🎯 Roamer detected (lane_role=4, lane=2, Radiant) - assigning to off lane (lane 3 equivalent)`);
          } else {
            laneKey = 'safe'; // Dire offlane is lane 1
            console.log(`🎯 Roamer detected (lane_role=4, lane=2, Dire) - assigning to safe lane (lane 1 equivalent)`);
          }
        } else {
          laneKey = 'off'; // Default roamers to offlane
          console.log(`🎯 Roamer detected (lane_role=4, lane=${lane}) - defaulting to off lane`);
        }
      }

      if (!laneKey) {
        console.warn(`⚠️ Player ${index + 1} skipped (lane=${lane}, lane_role=${lane_role}) - unhandled case`);
        return;
      }

      // Convert OpenDota numeric ID to string ID
      const heroStringId = OPENDOTA_HERO_ID_MAP[hero_id];

      if (!heroStringId) {
        console.warn(`⚠️ OpenDota Hero ID ${hero_id} not found in mapping - creating placeholder`);
        // Create a placeholder hero so slot is still visible
        const placeholderHero = {
          id: `unknown_${hero_id}`,
          name: `Unknown Hero (ID: ${hero_id})`,
          imageUrl: '',
          attributes: {}
        };
        matchups[laneKey][team].push(placeholderHero);
        return;
      }

      // Get hero object from HeroData
      const heroObject = HeroData[heroStringId];

      if (heroObject) {
        console.log(`✅ Adding ${heroObject.name} to ${team} ${laneKey} lane (OpenDota lane=${lane})`);
        matchups[laneKey][team].push(heroObject);
      } else {
        console.warn(`⚠️ Hero "${heroStringId}" not found in HeroData - creating placeholder`);
        // Create a placeholder hero so slot is still visible
        const placeholderHero = {
          id: heroStringId,
          name: heroStringId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          imageUrl: '',
          attributes: {}
        };
        matchups[laneKey][team].push(placeholderHero);
      }
    });

    console.log('📊 Final laning matchups (no swap needed - already correct):', {
      safe: { radiant: matchups.safe.radiant.map(h => h.name), dire: matchups.safe.dire.map(h => h.name) },
      mid: { radiant: matchups.mid.radiant.map(h => h.name), dire: matchups.mid.dire.map(h => h.name) },
      off: { radiant: matchups.off.radiant.map(h => h.name), dire: matchups.off.dire.map(h => h.name) }
    });

    // Return directly - no swapping needed because we already assigned to physical map positions
    return matchups;
  }

  static parseMatchDataToPicksFormat(matchData) {
    if (!matchData || !matchData.scenariosFirstPick || !Array.isArray(matchData.scenariosFirstPick)) {
      throw new Error('Invalid match data format');
    }

    // Get the first scenario's picks
    const firstScenario = matchData.scenariosFirstPick[0];

    if (!firstScenario || !Array.isArray(firstScenario.picks)) {
      throw new Error('No picks found in match data');
    }

    // Convert the external format to our picks format
    const picks = Array(24).fill(null);

    firstScenario.picks.forEach((pick, index) => {
      if (pick && pick.hero && index < 24) {
        picks[index] = {
          hero: {
            id: pick.hero.id,
            name: pick.hero.name,
            imageUrl: pick.hero.imageUrl,
            attributes: pick.hero.attributes
          },
          isBanned: pick.isBanned || false,
          position: index + 1
        };
      }
    });

    const result = {
      picks,
      title: matchData.title || `Match ${matchData.id || 'Unknown'}`,
      teamName: matchData.teamName || '',
      tournament: matchData.tournament || '',
      date: matchData.date || new Date().toISOString().slice(0, 10),
      originalData: matchData
    };

    return result;
  }

  static async loadMatchDataFile() {
    try {
      // Create a file input element
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.json';
      
      return new Promise((resolve, reject) => {
        input.onchange = async (e) => {
          const file = e.target.files[0];
          if (!file) {
            reject(new Error('No file selected'));
            return;
          }
          
          try {
            const text = await file.text();
            const data = JSON.parse(text);
            resolve(data);
          } catch (error) {
            reject(new Error(`Failed to parse file: ${error.message}`));
          }
        };
        
        input.oncancel = () => {
          reject(new Error('File selection cancelled'));
        };
        
        input.click();
      });
    } catch (error) {
      console.error('Error loading match data file:', error);
      throw error;
    }
  }
}

export default MatchDataService;